<template>
  <div :class="classObj" class="app-wrapper">
    <!-- Sticky Navbar -->
    <Navbar class="navbar-sticky" />
    <div v-if="classObj.mobile && sidebar.opened" class="drawer-bg" @click="handleClickOutside" />
    <!-- Sidebar and Main Content -->
    <div class="content-container">
      <Sidebar class="sidebar-container" />
      <div class="main-container">
        <AppMain />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component } from 'vue-property-decorator'
import { mixins } from 'vue-class-component'
import { AppModule, DeviceType } from '@/store/modules/app'
import { AppMain, Navbar, Sidebar } from './components'
import ResizeMixin from './mixin/resize'

@Component({
  name: 'AdminLayout',
  components: {
    AppMain,
    Navbar,
    Sidebar
  }
})
export default class extends mixins(ResizeMixin) {
  get classObj() {
    return {
      hideSidebar: !this.sidebar.opened,
      openSidebar: this.sidebar.opened,
      withoutAnimation: this.sidebar.withoutAnimation,
      mobile: this.device === DeviceType.Mobile
    }
  }

  private handleClickOutside() {
    AppModule.CloseSideBar(false)
  }
}
</script>

<style lang="scss" scoped>
/* Define variables for styling */
$navbar-height: 60px; /* Height of the navbar */
$sidebar-width: 240px; /* Width of the sidebar */
$header-bg-color: #ffffff; /* Header background color */
$sidebar-bg-color: #f8f9fa; /* Sidebar background color */
$content-bg-color: #ffffff; /* Main content background color */

.app-wrapper {
  position: relative;
  height: 100%;
  width: 100%;
}

.navbar-sticky {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: $navbar-height;
  z-index: 1002;
  background-color: $header-bg-color;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  padding: 0 16px;
}

.drawer-bg {
  background: rgba(0, 0, 0, 0.3);
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 999;
}

.content-container {
  display: flex;
  flex-direction: row;
  padding-top: $navbar-height; /* Adjust content position for the sticky navbar */
}

.sidebar-container {
  position: fixed;
  top: $navbar-height; /* Start below the navbar */
  left: 0;
  width: $sidebar-width;
  height: calc(100vh - $navbar-height);
  background-color: $sidebar-bg-color;
  z-index: 1001;
  overflow-y: auto;
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.main-container {
  flex-grow: 1;
  background-color: $content-bg-color;
  min-height: calc(100vh - $navbar-height);
  margin-left: $sidebar-width; /* Sidebar pushes content to the right */
  transition: margin-left 0.3s ease;
}

/* When Sidebar is Hidden */
.hideSidebar {
  .main-container {
    margin-left: 0; /* Content occupies full width when sidebar is hidden */
  }

  .sidebar-container {
    transform: translateX(-$sidebar-width); /* Sidebar slides out of view */
    opacity: 0;
    visibility: hidden;
  }
}

/* Mobile-Specific Styles */
.mobile {
  .main-container {
    margin-left: 0;
  }

  .sidebar-container {
    transform: translateX(-$sidebar-width);
    pointer-events: none;
  }

  &.openSidebar .sidebar-container {
    transform: translateX(0);
    pointer-events: all;
  }
}

.withoutAnimation {
  .main-container,
  .sidebar-container {
    transition: none;
  }
}
</style>
